import React, { useEffect, useMemo } from "react";
import { useLocale } from "lib/hooks/useLocale";
import { PaymentMode, PaymentModeType } from "lib/types/payments";
import { paymentMethodsConfig } from "lib/utils/payments";
import {
  extractPrepaidDiscountOffString,
  getAutomationPayloadForMethod,
  isEmptyObj,
  isIndianAddressWithForeignPhone,
} from "lib/utils/helpers";
import { usePaymentContext } from "lib/contexts/PaymentProvider";
import useSendAnalyticsEvent from "lib/hooks/useAnalytics";
import { analyticsEvents, analyticsTypes } from "lib/utils/constants";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { errorToast } from "lib/utils/toasters";
import { useUserContext } from "lib/contexts/UserProvider";
import { useCheckoutErrors } from "lib/hooks/useCheckoutErrors";
import { useAddressValidation } from "lib/hooks/useAddressValidation";
import { floorToTwoDecimals } from "lib/utils/helpers";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { usePopupOrchestrator } from "lib/contexts/PopupOrchestratorProvider";

interface UseMethodCardProps {
  modeData: PaymentMode;
  inFocus?: boolean;
}

export const useMethod = ({ modeData, inFocus }: UseMethodCardProps) => {
  const { t } = useLocale();
  const {
    state: { billing, shippingHandles, checkoutValidations, hasDefaultShippingHandleSelected, partnerOffers },
    actions: { setCheckoutView, setCheckoutModal, setActiveComponent },
  } = useCheckoutContext();
  const {
    state: { paymentMethods },
    actions: { handlePaymentMethodSelected, handleUnapplicablePaymentMethod, setPaymentMethodDialog },
  } = usePaymentContext();
  const {
    state: { user },
  } = useUserContext();
  const {
    state: { checkoutMetadata },
  } = useMerchantContext();
  const { openPopup } = usePopupOrchestrator();
  const { sendAnalyticsEvent } = useSendAnalyticsEvent();
  const { triggerCheckoutErrorInfoToast, isItemNotServicable } = useCheckoutErrors();

  const { verifyDefaultAddress } = useAddressValidation();

  const { initialCheckoutStep } = checkoutMetadata ?? {};
  const { name, discount, metadata, context, modeDiscountMetadata, isAvailable, unavailabilityReasons } =
    modeData;

  const discountMetadata = modeDiscountMetadata;

  const { title } = metadata || {};
  const discountCondition =
    extractPrepaidDiscountOffString(discountMetadata?.concessionAmount, discountMetadata?.deductionType) ??
    "";

  const hasPrepaidFreebie = Boolean(
    discountMetadata &&
      !isEmptyObj(discountMetadata) &&
      discountMetadata.isFreebie,
  );

  const freebiePrice = hasPrepaidFreebie
    ? (discountMetadata?.freebieAmount || 0)
    : 0;

  const [invalidOfferDialogOpen, setInvalidOfferDialogOpen] = React.useState(false);
  const handleInvalidOfferDialogClose = () => {
    setInvalidOfferDialogOpen(false);
  };

  useEffect(() => {
    if (inFocus) {
      sendAnalyticsEvent({
        eventName: analyticsEvents.FLO_PAYMENT_TILE_SELECTED,
        eventType: "click",
        metaData: {
          methodName: Boolean(title) ? title : paymentMethodsConfig[name]?.header,
          methodType: name,
        },
      });
    }
  }, [inFocus, name, sendAnalyticsEvent, title]);

  const handleMethodClick = () => {
    const isAddressValid = verifyDefaultAddress();
    if (isAvailable) {
      if (!Boolean(user?.addresses?.length) && initialCheckoutStep === "PAYMENTS") {
        openPopup("ADDRESS_NEW", {});
        return;
      }

      if (initialCheckoutStep === "PAYMENTS" && !isAddressValid) {
        if (!Boolean(checkoutValidations?.address?.metadata)) {
          openPopup("ADDRESS_LIST", {});
          errorToast(
            isIndianAddressWithForeignPhone(user?.default_shipping_address)
              ? t("indian_phone_required_for_indian_address")
              : t("serviceability_error"),
          );
          return;
        }
        openPopup("ADDRESS_EDIT", { addressId: checkoutValidations?.address?.metadata?.addressId ?? "" });
        return;
      }

      if (isItemNotServicable) {
        setCheckoutModal("UNSERVICEABLE_PRODUCTS");
        return;
      }

      if (!Boolean(shippingHandles?.length) && initialCheckoutStep !== "PAYMENTS") {
        triggerCheckoutErrorInfoToast(t("serviceability_error"));
        handleUnapplicablePaymentMethod(name, unavailabilityReasons);
        setCheckoutView("ADDRESS_LIST");
        return;
      }

      if (!Boolean(shippingHandles?.length) && initialCheckoutStep === "PAYMENTS") {
        triggerCheckoutErrorInfoToast(t("serviceability_error"));
        handleUnapplicablePaymentMethod(name, unavailabilityReasons);
        setActiveComponent("SHIPPING_ADDRESS_SECTION");
        return;
      }

      const hasSplit = paymentMethods.find((method: PaymentMode) => method.name === "SPLIT_COD");
      if (name === "COD" || (name === "SPLIT_COD" && context !== "SPLIT_COD")) {
        const hasBlockingPartnerOffer = partnerOffers?.some(
          (o) => o.blocks_cod && (o.offer_state === "COLLECTED" || o.offer_state === "ALREADY_APPLIED"),
        );
        if (hasBlockingPartnerOffer) {
          return setInvalidOfferDialogOpen(true);
        }
      }
      if (name === "COD" && !hasSplit) {
        const methodData = paymentMethods?.find((e: PaymentMode) => e.name === "COD");
        sendAnalyticsEvent({
          eventName: analyticsEvents.FLO_PAYMENT_METHOD_SELECTED,
          eventFor: [analyticsTypes.SF_ANALYTICS, analyticsTypes.FACEBOOK_PIXEL],
          eventType: "click",
          metaData: {
            methodType: name,
            automationData: getAutomationPayloadForMethod(methodData),
          },
        });
        if (!Boolean(hasDefaultShippingHandleSelected)) {
          openPopup("SHIPPING_HANDLES", {});
          return;
        }
        return setPaymentMethodDialog("COD_ORDER_PLACING");
      }
      if (name === "UPI_INTENT") {
        setPaymentMethodDialog("UPI_INTENT");
        return;
      }
      return handlePaymentMethodSelected(name, context as "SPLIT_COD");
    }
    const hasShippingBlocker = unavailabilityReasons.some((e) => e.type === "SHIPPING_HANDLES");
    if (hasShippingBlocker) {
      return handleUnapplicablePaymentMethod(name, unavailabilityReasons);
    }
    const hasUnavailableOffers = unavailabilityReasons.some((e) => e.type === "INVALID_DISCOUNTS");
    const hasBlockedPaymentModes = unavailabilityReasons.some((e) => e.type === "PAYMENT_MODES");
    if (hasUnavailableOffers || hasBlockedPaymentModes) {
      return setInvalidOfferDialogOpen(true);
    }
    return handleUnapplicablePaymentMethod(name, unavailabilityReasons);
  };

  const isInSplitCod = context === "SPLIT_COD";

  const hasDiscountOrFreebie = !isInSplitCod && (Boolean(discount) || hasPrepaidFreebie);

  const concessionAmount = floorToTwoDecimals(
    (hasPrepaidFreebie ? freebiePrice : discountMetadata?.concessionAmount) || 0,
  );

  const offerText = useMemo(() => {

    const activeDiscountMetadata = discountMetadata || null;
    const deductionType = activeDiscountMetadata?.deductionType;

    if (activeDiscountMetadata?.isCustomHeader && activeDiscountMetadata?.isCustomHeader === "CUSTOM") {
      return activeDiscountMetadata?.header?.replaceAll(
        "<value>",
        Boolean(activeDiscountMetadata?.maximumConcessionAmount)
          ? floorToTwoDecimals(modeData?.discount).toString()
          : concessionAmount.toString(),
      );
    }
    if (hasPrepaidFreebie) {
      if (discountMetadata?.freebies && discountMetadata?.freebies > 1) {
        return `Get items worth ₹${concessionAmount}`;
      }
      return `Get an item worth ₹${concessionAmount}`;
    }
    if (deductionType === "FLAT") {
      return `Get ₹${concessionAmount} off`;
    }
    if (Boolean(activeDiscountMetadata?.maximumConcessionAmount)) {
      return `Get ₹${floorToTwoDecimals(modeData?.discount)} off`;
    }
    return `Extra ${concessionAmount}% off`;
  }, [modeData, concessionAmount, hasPrepaidFreebie, discountMetadata]);

  return {
    billing,
    invalidOfferDialogOpen,
    setInvalidOfferDialogOpen,
    handleInvalidOfferDialogClose,
    freebiePrice,
    hasPrepaidFreebie,
    discountCondition,
    hasDiscountOrFreebie,
    handleMethodClick,
    offerText,
  };
};
